import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api.js';
import { t } from '../i18n.js';
import ApiGegevens from '../components/ApiGegevens.jsx';
import OntvangenVia, { standaard } from '../components/OntvangenVia.jsx';

/**
 * Een bron toevoegen, zoals in het prototype. Zie docs/besluiten/bronnen-toevoegen-en-verwijderen.md.
 * De server controleert alles nog een keer; wat hij weigert, staat bij het veld.
 *
 * Vanuit *Onbekende afzenders* komt dit formulier met naam, adres en klant al ingevuld.
 */
const SOORTEN = [
  { id: 'ftd-fmc', label: 'FTD, beheerd door FMC', uitleg: 'Syslog en IPS-events via de platforminstellingen van de FMC' },
  { id: 'ftd-fdm', label: 'FTD met FDM', uitleg: 'Standalone, beheerd op het apparaat zelf' },
  { id: 'fmc', label: 'FMC', uitleg: 'Het beheerplatform: auditlog van alle apparaten eronder' },
  { id: 'esa', label: 'Cisco ESA', uitleg: 'Secure Email Gateway: mail-logs, beheerlogins en status' },
  { id: 'ise', label: 'Cisco ISE (een node)', uitleg: 'Netwerktoegang: wie op welk adres zit, logins en beheer. Een bron per node.' },
];

/* Voorbeelden en uitleg die bij de soort passen: een ISE-node is geen firewall. Voorbeelden worden niet vertaald, de uitleg wel. */
const voorbeelden = {
  firewall: { naam: 'FTD-KANTOOR-01', versie: '7.4.2', ip: '10.62.8.1', beheer: 'https://10.62.8.1' },
  fmc: { naam: 'FMC-01', versie: '7.4.2', ip: '10.32.200.20', beheer: 'https://10.32.200.20' },
  esa: { naam: 'ESA-01', versie: '16.0', ip: '10.32.8.40', beheer: 'https://10.32.8.40' },
  ise: { naam: 'ISE-01', versie: '3.5', ip: '10.32.200.111', beheer: 'https://10.32.200.111/admin' },
};
const HULP = {
  firewall: { naam: 'Zoals hij in syslog staat (de hostname van de firewall)', ip: 'Zet de firewall geen naam in zijn berichten, dan herkent ADTS ZERO hem hieraan' },
  fmc: { naam: 'Zoals hij in syslog staat (de hostname van de FMC)', ip: 'Zet de FMC geen naam in zijn berichten, dan herkent ADTS ZERO hem hieraan' },
  esa: { naam: 'Een naam voor de ESA, bijvoorbeeld zijn hostname', ip: 'Verplicht: een ESA zet geen naam in zijn berichten' },
  ise: { naam: 'De hostname van deze ISE-node (PAN, MnT of PSN)', ip: 'Verplicht: ADTS ZERO herkent een ISE-node aan zijn adres' },
};
const voorbeeld = (kind) => {
  const k = voorbeelden[kind] ? kind : 'firewall';
  return { ...voorbeelden[k], hulpNaam: HULP[k].naam, hulpIp: HULP[k].ip };
};

export default function BronNieuw({ mij, klant }) {
  const [zoek] = useSearchParams();
  const naar = useNavigate();
  const client = useQueryClient();
  const eerste = zoek.get('klant') ?? (klant !== 'alle' ? klant : String(mij.tenants[0]?.id ?? ''));
  const [v, zetV] = useState({
    tenantId: eerste, kind: 'ftd-fdm', managedBy: '', profile: 'internet', tacacs: false,
    name: zoek.get('naam') ?? '', version: '', syslogIp: zoek.get('ip') ?? '', mgmtAddress: '', location: '', locationId: '', description: '',
  });
  const [fouten, zetFouten] = useState({});
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  /* Getest in het scherm, dan pas mee met het formulier (alleen bij een FMC). */
  const [apiG, zetApiG] = useState({ lezen: null, uitrollen: null });
  const [metUitrol, zetMetUitrol] = useState(false);
  const zet = (k) => (e) => zetV((x) => ({ ...x, [k]: e.target.value }));
  const vb = voorbeeld(v.kind);
  const apiFout = { lezen: fouten[`api.lezen`], uitrollen: fouten[`api.uitrollen`] };

  /* De FMC's van deze klant, uit dezelfde lijst als het bronnenscherm. */
  const lijst = useQuery({ queryKey: ['bronnen', v.tenantId], queryFn: () => api.bronnen(v.tenantId), enabled: Boolean(v.tenantId) });
  const fmcs = (lijst.data?.data.sources ?? []).filter((s) => s.kind === 'fmc' && !s.removed);
  const sensoren = lijst.data?.data.receivers ?? [];
  /* De locaties (vestigingen) van deze klant, om de hardware aan een vestiging te koppelen (docs/besluiten/klant-locaties.md). */
  const locaties = (lijst.data?.data.locations ?? []).filter((l) => String(l.tenantId) === String(v.tenantId));
  /* Via welke sensor: eerst de standaard voor deze klant, tot iemand zelf kiest. */
  const [via, zetVia] = useState(null);
  const viaWaarde = via ?? standaard(sensoren, v.tenantId);
  const klantNaam = mij.tenants.find((k) => String(k.id) === String(v.tenantId))?.name ?? '';

  async function bewaar(e) {
    e.preventDefault();
    zetFouten({}); zetMelding(null); zetBezig(true);
    try {
      const invoer = { ...v, managedBy: v.kind === 'ftd-fmc' ? v.managedBy : '', sensorId: viaWaarde };
      if (v.kind === 'fmc' && (apiG.lezen || apiG.uitrollen)) {
        invoer.api = { ...(apiG.lezen ? { lezen: apiG.lezen } : {}), ...(metUitrol && apiG.uitrollen ? { uitrollen: apiG.uitrollen } : {}) };
      }
      const { data } = await api.maakBron(invoer);
      await client.invalidateQueries({ queryKey: ['bronnen'] });
      naar(`/bronnen/${data.id}`);
    } catch (err) {
      zetFouten(err.details ?? {});
      zetMelding(t(err.message));
      zetBezig(false);
    }
  }

  /* Label en hulptekst komen al vertaald binnen: dan ziet de controle op vertalingen ze ook. */
  const veld = (k, label, invoer, hulp) => (
    <label className={`field ${fouten[k] ? 'bad' : ''}`}>
      <span>{label}</span>{invoer}
      {fouten[k] ? <span className="err">{t(fouten[k])}</span> : hulp ? <small>{hulp}</small> : null}
    </label>
  );

  return (
    <>
      <Link className="crumb" to="/bronnen">← {t('Bronnen')}</Link>
      <div className="page-head"><div>
        <h1>{t('Bron toevoegen')}</h1>
        <p className="sub">{t('ADTS ZERO gaat de bron bewaken zodra het eerste syslogbericht binnenkomt. Daarna krijg je de stappen om syslog in te stellen.')}</p>
      </div></div>
      <div className="grid-2">
        <section className="card">
          <form className="form" onSubmit={bewaar} noValidate>
            {melding && <div className="melder rood">{melding}</div>}
            {veld('tenantId', t('Klant'), (
              <select value={v.tenantId} onChange={(e) => { zet('tenantId')(e); zetVia(null); zetV((x) => ({ ...x, locationId: '' })); }}>
                {mij.tenants.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            ))}
            {veld('sensorId', t('Ontvangen via'), <OntvangenVia sensoren={sensoren} tenantId={v.tenantId} waarde={viaWaarde} opWijzig={zetVia} />,
              t('De sensor die de syslog van deze bron ontvangt. Een eigen sensor bij de klant, of rechtstreeks naar ZRO011.'))}
            <div className="field"><span>{t('Soort')}</span>
              <div className="kinds">
                {SOORTEN.map((s) => (
                  <label key={s.id} className={s.uit ? 'off' : undefined}>
                    <span><input type="radio" name="kind" value={s.id} disabled={s.uit} checked={v.kind === s.id} onChange={zet('kind')} />{t(s.label)}</span>
                    <small>{t(s.uitleg)}</small>
                  </label>
                ))}
              </div>
            </div>
            {v.kind === 'ftd-fmc' && veld('managedBy', t('Beheerd door'), fmcs.length ? (
              <select value={v.managedBy} onChange={zet('managedBy')}>
                <option value="">{t('Kies de FMC')}</option>
                {fmcs.map((f) => <option key={f.id} value={f.id}>{t('{fmc} · beheert nu {n}', { fmc: f.name, n: f.manages })}</option>)}
              </select>
            ) : <div className="callout warn">{t('{klant} heeft nog geen FMC in ADTS ZERO. Voeg eerst de FMC toe, of kies FTD met FDM.', { klant: klantNaam })}</div>)}
            {v.kind === 'ise' && (
              <label className="vink"><input type="checkbox" checked={v.tacacs} onChange={(e) => zetV((x) => ({ ...x, tacacs: e.target.checked }))} />
                {t('Deze ISE doet ook TACACS+ voor switches en routers')}</label>
            )}
            {!['fmc', 'esa', 'ise'].includes(v.kind) && (
              <div className="field"><span>{t('Plaats in het netwerk')}</span>
                <div className="kinds">
                  <label><span><input type="radio" name="profile" value="internet" checked={v.profile === 'internet'} onChange={zet('profile')} />{t('Aan internet')}</span>
                    <small>{t('Geweigerd verkeer is ruis; logins en IPS tellen')}</small></label>
                  <label><span><input type="radio" name="profile" value="intern" checked={v.profile === 'intern'} onChange={zet('profile')} />{t('Intern')}</span>
                    <small>{t('Elke scan of weigering is het bekijken waard')}</small></label>
                </div>
              </div>
            )}
            <div className="two">
              {veld('name', t('Naam'), <input className="input" value={v.name} onChange={zet('name')} placeholder={vb.naam} autoComplete="off" />,
                t(vb.hulpNaam))}
              {veld('version', t('Softwareversie'), <input className="input" value={v.version} onChange={zet('version')} placeholder={vb.versie} />)}
            </div>
            <div className="two">
              {veld('syslogIp', t('Adres waarvandaan hij syslog stuurt'), <input className="input mono" value={v.syslogIp} onChange={zet('syslogIp')} placeholder={vb.ip} />,
                t(vb.hulpIp))}
              {veld('mgmtAddress', t('Beheeradres'), <input className="input mono" value={v.mgmtAddress} onChange={zet('mgmtAddress')} placeholder={vb.beheer} />)}
            </div>
            <div className="two">
              {veld('location', t('Plaats'), <input className="input" value={v.location} onChange={zet('location')} placeholder={t('Kantoor Capelle')} />)}
              {veld('locationId', t('Locatie (vestiging)'),
                <select value={v.locationId} onChange={zet('locationId')}>
                  <option value="">{locaties.length ? t('Nog niet gekoppeld') : t('Deze klant heeft nog geen locaties (Beheer › Klanten › Locaties)')}</option>
                  {locaties.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>,
                t('Waar deze hardware staat. Bepaalt naar welke vestiging de aanvallen op de kaart lopen.'))}
              {veld('description', t('Omschrijving'), <input className="input" value={v.description} onChange={zet('description')} />)}
            </div>
            {v.kind === 'fmc' && (
              <fieldset className="api-blok">
                <legend>{t('API van de FMC (niet verplicht)')}</legend>
                {apiFout.lezen && <div className="melder rood">{t(apiFout.lezen)}</div>}
                <ApiGegevens doel="lezen" sensorId={viaWaarde} standaardUrl={v.syslogIp ? `https://${v.syslogIp}` : ''} opKlaar={(g) => zetApiG((x) => ({ ...x, lezen: g }))} />
                {metUitrol ? (
                  <>
                    {apiFout.uitrollen && <div className="melder rood">{t(apiFout.uitrollen)}</div>}
                    <ApiGegevens doel="uitrollen" sensorId={viaWaarde} standaardUrl={v.syslogIp ? `https://${v.syslogIp}` : ''} opKlaar={(g) => zetApiG((x) => ({ ...x, uitrollen: g }))}
                      opAnnuleer={() => { zetMetUitrol(false); zetApiG((x) => ({ ...x, uitrollen: null })); }} />
                  </>
                ) : <div><button type="button" className="namelink" onClick={() => zetMetUitrol(true)}>{t('Ook een uitrolgebruiker instellen')}</button></div>}
                <small className="soft">{t('Het wachtwoord gaat versleuteld de database in en is daarna niet meer te zien, ook niet voor jou. Alleen wat getest is, wordt opgeslagen.')}</small>
              </fieldset>
            )}
            <div className="actions">
              <button className="btn primary" disabled={bezig}>{t('Bron toevoegen')}</button>
              <Link className="btn" to="/bronnen">{t('Annuleren')}</Link>
            </div>
          </form>
        </section>
        <div className="stack">
          <div className="callout">{t('Een nieuwe bron is niet stil zolang er nog nooit iets binnenkwam: hij wacht op zijn eerste bericht. Pas daarna gaat de bewaking op stilte aan.')}</div>
          {v.kind === 'ise' ? <div className="callout">{t('Voeg elke ISE-node apart toe: elke node stuurt syslog vanaf zijn eigen adres. Daarna krijg je de stappen voor ISE: het target ADTS ZERO en de categorieën.')}</div>
            : v.kind === 'esa' ? null
              : <div className="callout">{t('Een FTD onder een FMC voeg je toe naast de FMC zelf. De FMC levert het auditlog: wie zich aanmeldde, wie een deploy deed.')}</div>}
        </div>
      </div>
    </>
  );
}
